import React from 'react';
import { groupTurns } from '../../lib/transcriptParse';
import { cleanSpeakerName } from '../protocol/speakerLabels';
import { createHighlighter } from '../../utils/searchHighlight';
import { withHits } from '../searchHits';

/**
 * Расшифровка разговора: имя и время слева, речь справа.
 *
 * Раскладка «интервью» выбрана владельцем 22.08.2026 и отвечает канону
 * фирстиля: левый флаг, ось, воздух. Имя не рвёт строку, глаз читает речь одной
 * колонкой, время не мешается в тексте. На узком экране колонка сворачивается
 * в строку над репликой.
 *
 * Цвет здесь фирменный (малиновый #E50071 акцентом, чёрный текст, серые
 * подписи), а не дефолтный фиолетовый остального интерфейса — переход
 * остальных экранов решается отдельно.
 *
 * Квадрат перед именем кодирует состояние, а не украшает: закрашенный —
 * участник узнан по голосу, контурный — имя поставил человек или его нет.
 *
 * `highlight` — запрос поиска по Истории: вхождения в речи подсвечены
 * `<mark data-search-hit>`. Без него разметка прежняя.
 *
 * `focusAt` — «ЧЧ:ММ:СС» находки поиска по смыслу: первая фраза, начавшаяся не
 * раньше этого времени, помечена `data-search-at` и чуть подсвечена — к ней
 * прокручивает просмотр записи.
 */
const startSec = (timecode) => {
  const m = /(\d{1,2}):(\d{2}):(\d{2})/.exec(timecode || '');
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : null;
};

const TranscriptView = ({ fragments, recognizedLabels, showTimecodes = true, highlight, focusAt }) => {
  const turns = groupTurns(fragments);
  if (!turns.length) return null;
  // Заново на каждой отрисовке: предел подсветок считается по порядку реплик.
  const hl = highlight ? createHighlighter(highlight) : null;

  const recognized = new Set(recognizedLabels || []);
  const hasSpeakers = turns.some((turn) => turn.speaker);

  // Расшифровка без спикеров (обычная транскрибация, вставленный текст) —
  // просто набор абзацев в той же типографике, без пустой левой колонки.
  if (!hasSpeakers) {
    return (
      <div className="font-ui max-w-[68ch] space-y-3">
        {turns.map((turn, index) => (
          <p key={index} className="text-[15px] leading-[1.7] text-paper-ink">{withHits(turn.text, hl)}</p>
        ))}
      </div>
    );
  }

  // Фразы блока по отдельности: время у каждой своё, и склеенный абзац его
  // терял. Когда таймкоды выключены — абзац остаётся одним куском, как и был.
  const paragraphs = (turn) => (showTimecodes && (turn.parts || []).length > 1
    ? turn.parts
    : [{ timecode: turn.timecode, text: turn.text }]);

  // Какую фразу пометить для прокрутки: первую с началом ≥ focusAt.
  const focusSec = startSec(focusAt);
  let focusKey = null;
  if (focusSec !== null) {
    turns.some((turn, index) => paragraphs(turn).some((part, partIndex) => {
      const sec = startSec(part.timecode);
      if (sec !== null && sec >= focusSec) { focusKey = `${index}:${partIndex}`; return true; }
      return false;
    }));
  }

  const Timecode = ({ value }) => (value ? (
    <span className="block text-[11px] tabular-nums text-paper-ink-3 sm:mt-0.5">
      {value.replace(/[[\]]/g, '')}
    </span>
  ) : null);

  return (
    <div className="font-ui">
      {turns.map((turn, index) => {
        const name = cleanSpeakerName(turn.speaker) || 'Без имени';
        const isRecognized = recognized.has(turn.speaker) || recognized.has(name);
        return (
          <article
            key={`${turn.speaker || 'no'}-${index}`}
            className="grid grid-cols-1 sm:grid-cols-[9.5rem_1fr] gap-x-6 gap-y-1 py-4 border-t border-paper-line first:border-t-0 first:pt-0"
          >
            {/* Имя — отдельной строкой над репликами, а не в одном ряду с
                первой фразой. Иначе первая фраза стоит на уровне имени, а все
                следующие — на уровне своих времён, и колонка речи начинается
                со сдвига (замечание владельца 05.09.2026). Теперь строки
                одинаковы: слева время, справа речь, у всех фраз без исключения. */}
            <header className="flex items-start gap-2 sm:col-span-2">
              <span
                aria-hidden="true"
                className={`mt-[5px] w-2 h-2 shrink-0 rounded-full ${isRecognized
                  ? 'bg-paper-accent'
                  : 'bg-paper-track'}`}
              />
              <span
                className="font-display text-[11px] font-bold uppercase tracking-[0.08em] text-paper-ink"
                title={isRecognized ? 'Узнан по голосу' : undefined}
              >
                {name}
              </span>
            </header>
            {paragraphs(turn).map((part, partIndex) => (
              <React.Fragment key={partIndex}>
                {/* Отступ 1rem повторяет ширину квадрата статуса: времена
                    встают под именем ровным столбцом, а не съезжают влево. */}
                <div className="pl-4">{showTimecodes && <Timecode value={part.timecode} />}</div>
                <p
                  data-search-at={focusKey === `${index}:${partIndex}` ? 'true' : undefined}
                  className={`max-w-[68ch] text-[15px] leading-[1.7] text-paper-ink ${
                    focusKey === `${index}:${partIndex}` ? 'rounded-[4px] bg-paper-accent/10' : ''}`}
                >
                  {withHits(part.text, hl)}
                </p>
              </React.Fragment>
            ))}
          </article>
        );
      })}
    </div>
  );
};

export default TranscriptView;
